<script setup lang="ts">
export type TButtonType = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8';

type TButtonStyles = {
  normal: string;
  hover: string;
  active: string;
  disabled: string;
};

const props = withDefaults(
  defineProps<{ disabled?: boolean; title?: string; type?: TButtonType; ariaLabel?: string }>(),
  {
    title: '',
    disabled: false,
    type: 1,
    ariaLabel: 'button',
  },
);

const styleClasses: Record<TButtonType, TButtonStyles> = {
  1: {
    normal: 'px-4 py-2 text-big text-gray-100 bg-gray-900 border-gray-900 border',
    hover: 'hover:bg-gray-800',
    active: 'active:translate-y-[1px]',
    disabled: 'disabled:pointer-events-none disabled:bg-gray-800 disabled:text-gray-500/50',
  },

  2: {
    normal: 'px-4 py-2 text-sub text-gray-100 bg-gray-900',
    hover: 'hover:bg-gray-800',
    active: 'active:translate-y-[1px]',
    disabled: 'disabled:pointer-events-none disabled:bg-gray-800 disabled:text-gray-500/50',
  },

  3: {
    normal: 'px-[15px] py-[7px] text-sub text-gray-900 bg-gray-100 border border-gray-500',
    hover: 'hover:bg-gray-300',
    active: 'active:translate-y-[1px]',
    disabled: 'disabled:pointer-events-none  disabled:text-gray-900/50',
  },

  4: {
    normal: 'text-big font-medium text-gray-900',
    hover: 'hover:text-gray-800',
    active: 'active:translate-y-[1px]',
    disabled: 'disabled:pointer-events-none disabled:text-gray-900/50',
  },
  5: {
    normal: 'text-big font-medium text-gray-100',
    hover: 'hover:text-gray-500',
    active: 'active:translate-y-[1px]',
    disabled: 'disabled:pointer-events-none disabled:text-gray-500/50',
  },
  6: {
    normal: 'w-[42px] h-[42px] rounded-full bg-gray-500 flex justify-center items-center',
    hover: 'hover:text-blue-600',
    active: 'active:translate-y-[1px]',
    disabled: 'disabled:pointer-events-none disabled:text-gray-500/50 disabled:bg-gray-500/50',
  },
  7: {
    normal: 'w-9 h-9 border border-gray-300 bg-gray-300 rounded-full',
    hover: 'hover:border-gray-500 hover:bg-gray-300',
    active: 'active:translate-y-[1px]',
    disabled: 'disabled:pointer-events-none disabled:bg-gray-100 disabled:text-gray-900/50',
  },
  8: {
    normal: 'w-fit h-fit px-4 py-2 border border-gray-300 bg-gray-300 rounded',
    hover: 'hover:border-gray-500 hover:bg-gray-300',
    active: 'active:translate-y-[1px]',
    disabled: 'disabled:pointer-events-none disabled:bg-gray-100 disabled:text-gray-900/50',
  },
};

const typedStyles = computed(
  () =>
    `${styleClasses[props.type].normal} `
    + `${styleClasses[props.type].hover} `
    + `${styleClasses[props.type].active} `
    + `${styleClasses[props.type].disabled} `,
  // + `${styleClasses[props.type].focus} `,
);
</script>

<template>
  <button
    :aria-label="ariaLabel"
    :disabled="disabled"
    class="cursor-pointer select-none text-center outline-none rounded flex gap-2 justify-center items-center whitespace-nowrap"
    :class="typedStyles"
  >
    <slot name="prepend" />
    <template v-if="title">
      {{ title }}
    </template>

    <slot name="append" />
  </button>
</template>

<style scoped lang="scss"></style>
